
import React from "react";
import { 
  Carousel, 
  CarouselContent, 
  CarouselItem, 
  CarouselNext, 
  CarouselPrevious 
} from "@/components/ui/carousel";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowRight, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";

interface Testimonial {
  restaurantName: string;
  ownerName: string;
  city: string;
  quote: string;
  metrics: {
    title: string;
    value: string;
    change: string;
  }[];
}

const testimonials: Testimonial[] = [
  {
    restaurantName: "Bella Napoli Pizza",
    ownerName: "Marco Rossi",
    city: "San Francisco, CA",
    quote: "Since implementing EatsBoost, our customer retention has increased dramatically. The digital loyalty cards make it so easy for customers to keep coming back! We've seen a significant increase in repeat business and our average order value has gone up as well.",
    metrics: [
      { title: "Customer Retention", value: "32%", change: "Increase" },
      { title: "Average Order Value", value: "$28.50", change: "+15%" },
      { title: "New Customers", value: "120+", change: "Monthly" }
    ]
  },
  {
    restaurantName: "Sushi Star",
    ownerName: "Yuki Tanaka",
    city: "Seattle, WA",
    quote: "EatsBoost transformed how we reward our regulars. The app is intuitive, and customers love collecting points toward their next meal. We've been able to track customer preferences and tailor our menu accordingly, resulting in higher satisfaction rates and more frequent visits.",
    metrics: [
      { title: "Repeat Visits", value: "45%", change: "Increase" },
      { title: "Revenue Growth", value: "23%", change: "Year-over-Year" },
      { title: "Customer Satisfaction", value: "4.8/5", change: "Rating" }
    ]
  },
  {
    restaurantName: "Spice House",
    ownerName: "Priya Patel",
    city: "Chicago, IL",
    quote: "The data insights from EatsBoost helped us understand our customers better. We've optimized our menu and marketing based on actual customer preferences. This targeted approach has significantly improved our marketing ROI and increased our regular customer base.",
    metrics: [
      { title: "Marketing ROI", value: "3.2x", change: "Higher" },
      { title: "Customer Frequency", value: "2.5x", change: "Monthly Visits" },
      { title: "Digital Engagement", value: "87%", change: "Active Users" }
    ]
  },
  {
    restaurantName: "Green Garden Café",
    ownerName: "Sarah Johnson",
    city: "Austin, TX",
    quote: "EatsBoost simplified our loyalty program and eliminated paper cards. Our customers appreciate the environmental benefit, and we've seen significant growth. The transition was seamless, and now we have valuable data about our customers that we never had access to before.",
    metrics: [
      { title: "Paper Saved", value: "5,000+", change: "Cards Monthly" },
      { title: "Program Participation", value: "78%", change: "Of Customers" },
      { title: "Revenue Increase", value: "18%", change: "Quarter-over-Quarter" }
    ]
  }
];

const statsData = [
  { label: "Offers Redeemed", value: "25" },
  { label: "Regular Visits Completed", value: "9" },
  { label: "Boost Cards Sold", value: "12" },
  { label: "Loving Users", value: "12" }
];

const Testimonials = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col items-center mb-10">
          <div className="flex items-center justify-center">
            <Logo />
            <h2 className="text-3xl font-bold text-black font-montserrat ml-4">EatsBoost</h2>
          </div>
          <p className="text-gray-600 text-center mt-4 max-w-md">
            A local startup, helping local restaurants compete with the big guys through a beautiful, consumer-facing app
          </p>
        </div>
        
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            Real Results for Restaurant Partners
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            See how <span className="font-bold text-eatsboost-orange">25+</span> restaurants like yours are growing their business with EatsBoost
          </p>
        </div>

        <h2 className="text-2xl font-bold text-center text-gray-900 mb-4">
          EatsBoost is Different Than Anything You've Been Pitched Before
        </h2>

        <Card className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <CardContent className="p-0">
            <div className="text-xl md:text-2xl text-gray-800 mb-6">
              SEO, email marketing, social media marketing — blah blah blah. You've heard it all before. It's time for something fundamentally different.
            </div>
            <div className="text-lg md:text-xl text-gray-700 mb-6">
              Here's the truth every owner knows, there's only 2 things that actually make your financials go up:
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li><span className="font-bold text-eatsboost-orange">More Visits</span></li>
                <li><span className="font-bold text-eatsboost-orange">Bigger Orders</span></li>
              </ul>
            </div>
            <div className="text-lg md:text-xl text-gray-700">
              EatsBoost helps make both happen by sending your best customers <span className="font-bold" style={{ color: "#006100" }}>profitable</span>, <span className="font-bold text-eatsboost-orange">time sensitive and exclusive offers every week</span> — and we handle everything, so there's no extra time or work needed from you.
            </div>
          </CardContent>
        </Card>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <h2 className="text-2xl font-semibold mb-2 text-gray-800">
            What Makes EatsBoost So Special
          </h2>
          <p className="text-lg text-gray-600 mb-6">
            Think of it as an exclusive loyalty program, available to only your best customers
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="flex flex-col items-start">
              <div className="bg-eatsboost-orange/10 p-4 rounded-full mb-4">
                <ArrowRight className="h-6 w-6 text-eatsboost-orange" />
              </div>
              <h3 className="text-lg font-medium mb-2">EatsBoost is Exclusive</h3>
              <p className="text-gray-600">
                We cap the number of customers who can add your restaurant—<span className="font-bold text-eatsboost-orange">on purpose</span>. This exclusivity creates <span className="font-bold text-eatsboost-orange">status</span>, which drives <span className="font-bold text-eatsboost-orange">urgency</span> and <span className="font-bold text-eatsboost-orange">usage</span>. The result? More visits and more revenue for you each month.
              </p>
            </div>
            
            <div className="flex flex-col items-start">
              <div className="bg-eatsboost-orange/10 p-4 rounded-full mb-4">
                <ArrowRight className="h-6 w-6 text-eatsboost-orange" />
              </div>
              <h3 className="text-lg font-medium mb-2">Customers Must Purchase a Boost Card to Add Your Restaurant</h3>
              <p className="text-gray-600">
                Exclusivity shouldn't be <span className="font-bold text-eatsboost-orange">free</span> or else it's not valued. When customers <span className="font-bold text-eatsboost-orange">pay</span> for a Boost Card, they're more <span className="font-bold text-eatsboost-orange">invested</span>—and more likely to redeem offers. That means higher satisfaction for them, and more profit for you. You buy these Boost Cards from us at wholesale and sell them at a nice profit.
              </p>
            </div>
            
            <div className="flex flex-col items-start">
              <div className="bg-eatsboost-orange/10 p-4 rounded-full mb-4">
                <ArrowRight className="h-6 w-6 text-eatsboost-orange" />
              </div>
              <h3 className="text-lg font-medium mb-2">Build a User Database</h3>
              <p className="text-gray-600">
                Most restaurants don't <span className="font-bold text-eatsboost-orange">track</span> customer behavior—EatsBoost does it for you. We capture visit patterns, offer redemptions, and preferences. Over time, this <span className="font-bold text-eatsboost-orange">data</span> powers smarter, more <span className="font-bold text-eatsboost-orange">personalized</span> offers so that we can give the right offer, to the right customer, at the right time. This data can also supercharge your decisions: identify top-performing dishes, tweak underperforming ones and optimize slow days.
              </p>
            </div>
          </div>
        </div>

        <Card className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <CardHeader className="px-0 pt-0">
            <CardTitle className="text-2xl font-semibold text-gray-800">
              Why Should the Big Guys Keep Winning
            </CardTitle>
            <CardDescription className="text-lg text-gray-600">
              With unlimited money and dedicated teams, the big guys have been outcompeting the local restaurants for years. EatsBoost is here to change that
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
              <Card className="shadow-md">
                <CardHeader>
                  <CardTitle className="text-xl">A Beautiful App for a Fraction of the Cost</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Building an app from the ground up costs <span className="font-bold text-eatsboost-orange">thousands</span>. We give all our partners the same app with <span className="font-bold text-eatsboost-orange">zero</span> upfront cost. And even better? Our fees are <span className="font-bold text-eatsboost-orange">value-driven</span>, meaning you only pay when we make a difference for you
                  </p>
                </CardContent>
              </Card>
              
              <Card className="shadow-md">
                <CardHeader>
                  <CardTitle className="text-xl">No Extra Effort</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    EatsBoost is built for <span className="font-bold text-eatsboost-orange">busy restaurant owners</span>. No dashboards, no campaigns, no extra staff. Just one <span className="font-bold text-eatsboost-orange">quick onboarding call</span>, and you're set—EatsBoost runs in the <span className="font-bold text-eatsboost-orange">background</span> while you focus on running your restaurant.
                  </p>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>

        <h2 className="text-2xl font-semibold mb-6 text-center text-gray-800">
          Success Stories from Our Partners
        </h2>
        
        <Carousel className="w-full">
          <CarouselContent>
            {testimonials.map((testimonial, index) => (
              <CarouselItem key={index} className="md:basis-1/1 lg:basis-1/1">
                <Card className="border-0 shadow-lg h-full">
                  <CardHeader className="bg-gray-800 text-white rounded-t-lg">
                    <CardTitle>{testimonial.restaurantName}</CardTitle>
                    <CardDescription className="text-gray-200">
                      {testimonial.ownerName}, Owner | {testimonial.city}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <ScrollArea className="h-40 mb-4">
                      <p className="italic text-gray-700">"{testimonial.quote}"</p>
                    </ScrollArea>
                    
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      {testimonial.metrics.map((metric, idx) => (
                        <div key={idx} className="text-center p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600">{metric.title}</p>
                          <p className="text-xl font-bold text-eatsboost-orange mt-1">{metric.value}</p>
                          <p className="text-xs text-gray-500">{metric.change}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                  <CardFooter className="border-t pt-4 flex justify-end">
                    <p className="text-sm text-gray-500">Results may vary by restaurant</p>
                  </CardFooter>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex justify-center mt-8">
            <CarouselPrevious className="relative static mr-4" />
            <CarouselNext className="relative static ml-4" />
          </div>
        </Carousel>

        <div className="mt-20 mb-12">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            A Disruptive Idea Making a Difference
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {statsData.map((stat, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md text-center">
                <p className="text-3xl font-bold text-eatsboost-orange mb-2">{stat.value}</p>
                <p className="text-gray-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
        
        <div className="mt-20 mb-12 bg-gray-800 rounded-xl shadow-lg overflow-hidden">
          <div className="py-12 px-8 md:px-12 text-white flex flex-col md:flex-row items-center justify-between">
            <div className="mb-8 md:mb-0 md:mr-6 text-center md:text-left">
              <h2 className="text-3xl font-bold mb-4">Ready to Boost Your Restaurant?</h2>
              <p className="text-gray-200 text-lg max-w-md">
                Schedule a 30-minute demo with our team to see how EatsBoost can make your financials go up.
              </p>
            </div>
            <Button 
              size="lg" 
              className="bg-eatsboost-orange text-white hover:bg-eatsboost-orange/90 rounded-full px-8 py-6 h-auto flex items-center" 
              onClick={() => window.open("https://calendly.com/eatsboost1/30min?month=2025-04", "_blank")}
            >
              <Calendar className="mr-2 h-5 w-5" />
              Book a Meeting
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Testimonials;
